<script setup>
import { computed, onMounted, ref } from 'vue';
import SimpleTagPicker from 'dashboard/components-next/simple-tag-picker/SimpleTagPicker.vue';

const playbooks = ref([]);
const establishments = ref([]);
const resourceFiles = ref([]);
const resultImages = ref([]);
const selectedId = ref('');
const activeTab = ref('general');
const isLoading = ref(false);
const isSaving = ref(false);
const isUploadingResource = ref(false);
const message = ref('');
const resourceFileInput = ref(null);
const resourceUpload = ref(emptyResourceUpload());
const form = ref(emptyForm());

const tabs = [
  { id: 'general', label: 'Geral' },
  { id: 'steps', label: 'Etapas' },
  { id: 'faq', label: 'FAQ' },
  { id: 'resources', label: 'Recursos' },
  { id: 'simulation', label: 'Simulacao com IA' },
  { id: 'payment', label: 'Pagamento' },
  { id: 'publication', label: 'Publicacao' },
];

const tagSuggestions = [
  { value: 'recepcao', label: 'Recepcao' },
  { value: 'avaliacao-online', label: 'Avaliacao online' },
  { value: 'avaliacao-presencial', label: 'Avaliacao presencial' },
  { value: 'entradas', label: 'Entradas' },
  { value: 'topo', label: 'Topo da cabeca' },
  { value: 'grisalho', label: 'Cabelos grisalhos' },
  { value: 'naturalidade', label: 'Naturalidade' },
  { value: 'manutencao', label: 'Manutencao' },
  { value: 'banho', label: 'Banho e lavagem' },
  { value: 'duracao', label: 'Duracao da protese' },
  { value: 'oferta', label: 'Oferta comercial' },
  { value: 'pagamento', label: 'Pagamento' },
];

function emptyStep() {
  return {
    key: '',
    title: '',
    entryConditions: '',
    requiredFieldsText: '',
    allowedActionsText: '',
    responseGuidelines: '',
    handoffConditions: '',
    isActive: true,
  };
}

function emptyFaq() {
  return {
    question: '',
    questionVariantsText: '',
    approvedAnswer: '',
    audioResourceFileId: '',
    audioSendMode: 'ask_permission',
    priority: 0,
    isActive: true,
  };
}

function emptyResourceUpload() {
  return { resourceType: 'approved_audio', title: '', tags: [], file: null };
}

function emptyPaymentProfile() {
  return { establishmentId: '', label: '', instructionTemplate: '', secretReference: '', isActive: true };
}

function emptyForm() {
  return {
    name: 'Atendimento comercial Zero14',
    toneOfVoice: '',
    draftMarkdown: '',
    handoffGuidelines: '',
    steps: [],
    faqs: [],
    resources: [],
    visualSimulationConfig: {
      enabled: true,
      noticeText: 'A simulacao visual com IA e opcional e serve apenas como referencia inicial.',
      requiredAngles: ['front', 'top', 'left', 'right'],
      photoTemplateResourceId: '',
      photoGuideCaption: 'Use esta imagem como exemplo. Envie fotos com boa luz, sem filtro e mostrando claramente frente, topo e laterais.',
      retentionDays: 30,
      maxVariants: 2,
      fallbackText: 'Nao consegui gerar sua simulacao agora. Vou encaminhar para nossa equipe continuar com voce por aqui.',
    },
    paymentProfiles: [],
  };
}

const selected = computed(() => playbooks.value.find(item => item.id === selectedId.value));
const versions = computed(() =>
  [...(selected.value?.attendance_playbook_versions || [])].sort((a, b) => b.version - a.version)
);

function splitList(value) {
  return String(value || '').split(',').map(item => item.trim()).filter(Boolean);
}

function payload() {
  return {
    ...form.value,
    steps: form.value.steps.map(item => ({
      ...item,
      requiredFields: splitList(item.requiredFieldsText),
      allowedActions: splitList(item.allowedActionsText),
    })),
    faqs: form.value.faqs.map(item => ({
      ...item,
      questionVariants: splitList(item.questionVariantsText),
      audioResourceFileId: item.audioResourceFileId || null,
      audioSendMode: item.audioSendMode || 'ask_permission',
    })),
    resources: form.value.resources.map(item => ({
      ...item,
      tags: item.tags,
    })),
  };
}

function select(item) {
  selectedId.value = item.id;
  activeTab.value = 'general';
  form.value = {
    name: item.name || '',
    toneOfVoice: item.tone_of_voice || '',
    draftMarkdown: item.draft_markdown || '',
    handoffGuidelines: item.handoff_guidelines || '',
    steps: [...(item.attendance_protocol_steps || [])]
      .sort((a, b) => a.position - b.position)
      .map(step => ({
        key: step.key || '',
        title: step.title || '',
        entryConditions: step.entry_conditions || '',
        requiredFieldsText: (step.required_fields || []).join(', '),
        allowedActionsText: (step.allowed_actions || []).join(', '),
        responseGuidelines: step.response_guidelines || '',
        handoffConditions: step.handoff_conditions || '',
        isActive: Boolean(step.is_active),
      })),
    faqs: (item.attendance_faq_entries || []).map(faq => ({
      question: faq.question || '',
      questionVariantsText: (faq.question_variants || []).join(', '),
      approvedAnswer: faq.approved_answer || '',
      audioResourceFileId: faq.audio_resource_file_id || '',
      audioSendMode: faq.audio_send_mode || 'ask_permission',
      priority: faq.priority || 0,
      isActive: Boolean(faq.is_active),
    })),
    resources: (item.attendance_resources || []).map(resource => ({
      resourceType: resource.resource_type || 'photo_template',
      title: resource.title || '',
      reference: resource.reference || '',
      libraryResourceId: resource.library_resource_id || '',
      tags: resource.tags || [],
      isActive: Boolean(resource.is_active),
    })),
    visualSimulationConfig: {
      enabled: item.visual_simulation_config?.enabled !== false,
      noticeText: item.visual_simulation_config?.notice_text || '',
      requiredAngles: item.visual_simulation_config?.required_angles || ['front', 'top', 'left', 'right'],
      photoTemplateResourceId: item.visual_simulation_config?.photo_template_resource_id || '',
      photoGuideCaption: item.visual_simulation_config?.photo_guide_caption || '',
      retentionDays: item.visual_simulation_config?.retention_days || 30,
      maxVariants: item.visual_simulation_config?.max_variants || 2,
      fallbackText: item.visual_simulation_config?.fallback_text || '',
    },
    paymentProfiles: (item.attendance_payment_profiles || []).map(profile => ({
      establishmentId: profile.establishment_id || '',
      label: profile.label || '',
      instructionTemplate: profile.instruction_template || '',
      secretReference: profile.secret_reference || '',
      isActive: Boolean(profile.is_active),
    })),
  };
}

async function load(preferredId = selectedId.value) {
  isLoading.value = true;
  message.value = '';
  try {
    const [playbooksResponse, establishmentsResponse, resourcesResponse, resultImagesResponse] = await Promise.all([
      fetch('/api/superwoot/attendance-directives'),
      fetch('/api/superwoot/establishments?includeInactive=true'),
      fetch('/api/superwoot/attendance-resources'),
      fetch('/api/superwoot/result-images'),
    ]);
    const [playbooksPayload, establishmentsPayload, resourcesPayload, resultImagesPayload] = await Promise.all([
      playbooksResponse.json(),
      establishmentsResponse.json(),
      resourcesResponse.json(),
      resultImagesResponse.json(),
    ]);
    if (!playbooksResponse.ok) throw new Error(playbooksPayload.error || 'Falha ao carregar diretrizes');
    if (!establishmentsResponse.ok) throw new Error(establishmentsPayload.error || 'Falha ao carregar unidades');
    if (!resourcesResponse.ok) throw new Error(resourcesPayload.error || 'Falha ao carregar biblioteca de recursos');
    if (!resultImagesResponse.ok) throw new Error(resultImagesPayload.error || 'Falha ao carregar galeria de resultados');
    playbooks.value = playbooksPayload.attendancePlaybooks || [];
    establishments.value = establishmentsPayload.establishments || [];
    resourceFiles.value = resourcesPayload.attendanceResourceFiles || [];
    resultImages.value = resultImagesPayload.resultImages || [];
    const item = playbooks.value.find(candidate => candidate.id === preferredId) || playbooks.value[0];
    if (item) select(item);
  } catch (error) {
    message.value = error.message;
  } finally {
    isLoading.value = false;
  }
}

function createNew() {
  selectedId.value = '';
  activeTab.value = 'general';
  form.value = emptyForm();
}

function move(items, index, offset) {
  const target = index + offset;
  if (target < 0 || target >= items.length) return;
  const [item] = items.splice(index, 1);
  items.splice(target, 0, item);
}

function chooseResourceFile(event) {
  resourceUpload.value.file = event.target.files?.[0] || null;
}

function resourceTypeLabel(type) {
  return {
    photo_template: 'Template de foto',
    result_gallery: 'Galeria de resultados',
    approved_audio: 'Áudio aprovado',
    document: 'Documento',
  }[type] || type;
}

const approvedAudioFiles = computed(() =>
  resourceFiles.value.filter(item => item.resource_type === 'approved_audio' && item.is_active !== false)
);

function resourceIsLinked(reference) {
  return form.value.resources.some(item => item.reference === reference);
}

function addResource(resource) {
  if (resourceIsLinked(resource.reference)) {
    message.value = 'Este recurso ja esta vinculado ao protocolo.';
    return;
  }
  form.value.resources.push(resource);
  message.value = 'Recurso vinculado. Salve o rascunho para confirmar.';
}

function useLibraryFile(item) {
  addResource({
    resourceType: item.resource_type,
    title: item.title,
    reference: `attendance_resource:${item.id}`,
    libraryResourceId: item.id,
    tags: item.tags || [],
    isActive: true,
  });
}

function useGalleryImage(item) {
  addResource({
    resourceType: 'result_gallery',
    title: item.title,
    reference: `result_image:${item.id}`,
    libraryResourceId: '',
    tags: item.tags || [],
    isActive: true,
  });
}

async function uploadResource() {
  if (!resourceUpload.value.file || !resourceUpload.value.title.trim()) {
    message.value = 'Informe um titulo e escolha o arquivo.';
    return;
  }
  isUploadingResource.value = true;
  message.value = '';
  try {
    const body = new FormData();
    Object.entries(resourceUpload.value).forEach(([key, value]) => {
      if (value !== null) body.append(key, value);
    });
    const response = await fetch('/api/superwoot/attendance-resources', { method: 'POST', body });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao enviar recurso');
    resourceFiles.value.unshift(result.attendanceResourceFile);
    useLibraryFile(result.attendanceResourceFile);
    resourceUpload.value = emptyResourceUpload();
    if (resourceFileInput.value) resourceFileInput.value.value = '';
  } catch (error) {
    message.value = error.message;
  } finally {
    isUploadingResource.value = false;
  }
}

async function save() {
  isSaving.value = true;
  message.value = '';
  try {
    const body = payload();
    if (selectedId.value) body.playbookId = selectedId.value;
    const response = await fetch('/api/superwoot/attendance-directives', {
      method: selectedId.value ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao salvar diretrizes');
    const id = selectedId.value || result.attendancePlaybook?.id;
    message.value = 'Rascunho salvo.';
    await load(id);
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

async function publish() {
  if (!selectedId.value) return;
  if (!window.confirm('Publicar este rascunho para uso do atendimento?')) return;
  isSaving.value = true;
  message.value = '';
  try {
    const response = await fetch('/api/superwoot/attendance-directives', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'publish', playbookId: selectedId.value }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao publicar diretrizes');
    message.value = `Versao ${result.publishedVersion.version} publicada.`;
    await load(selectedId.value);
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

async function restore(version) {
  if (!window.confirm(`Restaurar a versao ${version.version} como novo rascunho?`)) return;
  isSaving.value = true;
  message.value = '';
  try {
    const response = await fetch('/api/superwoot/attendance-directives', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'restore', playbookId: selectedId.value, versionId: version.id }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao restaurar versao');
    message.value = `Versao ${version.version} restaurada como rascunho.`;
    await load(selectedId.value);
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-n-background">
    <header class="border-b border-n-weak px-4 py-4 sm:px-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-xl font-medium text-n-slate-12">Diretrizes de atendimento</h1>
          <p class="mt-1 text-sm text-n-slate-10">Edite o protocolo comercial usado pela assistente sem alterar o workflow.</p>
        </div>
        <button class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white" type="button" @click="createNew">
          <span class="i-lucide-plus size-4" />
          Novo protocolo
        </button>
      </div>
    </header>

    <div class="grid min-h-0 flex-1 lg:grid-cols-[minmax(240px,320px)_1fr]">
      <aside class="border-b border-n-weak p-4 lg:border-b-0 lg:border-r">
        <p v-if="isLoading" class="text-sm text-n-slate-10">Carregando...</p>
        <p v-else-if="playbooks.length === 0" class="text-sm text-n-slate-10">Nenhum protocolo cadastrado.</p>
        <div class="space-y-2">
          <button v-for="item in playbooks" :key="item.id" type="button" class="w-full rounded-md border p-3 text-left" :class="item.id === selectedId ? 'border-n-brand bg-n-alpha-2' : 'border-n-weak hover:bg-n-alpha-1'" @click="select(item)">
            <span class="flex items-center justify-between gap-2">
              <strong class="text-sm text-n-slate-12">{{ item.name }}</strong>
              <span class="text-xs capitalize text-n-slate-10">{{ item.status }}</span>
            </span>
            <span class="mt-1 block text-xs text-n-slate-10">{{ item.attendance_protocol_steps?.length || 0 }} etapa(s)</span>
          </button>
        </div>
      </aside>

      <main class="overflow-y-auto p-4 sm:p-6">
        <form class="mx-auto max-w-4xl" @submit.prevent="save">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 class="text-base font-medium text-n-slate-12">{{ selectedId ? 'Editar protocolo' : 'Novo protocolo' }}</h2>
              <p class="mt-1 text-sm text-n-slate-10">As alteracoes ficam em rascunho ate a publicacao.</p>
            </div>
            <span v-if="selected?.published_version_id" class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11">Publicado</span>
          </div>

          <nav class="mt-5 flex gap-1 overflow-x-auto border-b border-n-weak">
            <button v-for="tab in tabs" :key="tab.id" type="button" class="shrink-0 border-b-2 px-3 py-2 text-sm" :class="activeTab === tab.id ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-10'" @click="activeTab = tab.id">
              {{ tab.label }}
            </button>
          </nav>

          <section v-if="activeTab === 'general'" class="mt-5 space-y-4">
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Nome do protocolo</span><input v-model="form.name" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Tom de voz</span><textarea v-model="form.toneOfVoice" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" /></label>
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Diretrizes comerciais</span><textarea v-model="form.draftMarkdown" rows="16" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 font-mono text-sm" /></label>
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Quando transferir para humano</span><textarea v-model="form.handoffGuidelines" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" /></label>
          </section>

          <section v-else-if="activeTab === 'steps'" class="mt-5 space-y-4">
            <div class="flex items-center justify-between gap-3"><p class="text-sm text-n-slate-10">Organize as etapas na ordem da conversa.</p><button type="button" class="inline-flex items-center gap-2 text-sm text-n-brand" @click="form.steps.push(emptyStep())"><span class="i-lucide-plus size-4" /> Nova etapa</button></div>
            <article v-for="(step, index) in form.steps" :key="index" class="rounded-md border border-n-weak p-4">
              <div class="flex items-center justify-between gap-3"><strong class="text-sm text-n-slate-12">Etapa {{ index + 1 }}</strong><div class="flex items-center gap-3"><button type="button" title="Mover etapa para cima" class="i-lucide-chevron-up size-4 text-n-slate-10 disabled:opacity-30" :disabled="index === 0" @click="move(form.steps, index, -1)" /><button type="button" title="Mover etapa para baixo" class="i-lucide-chevron-down size-4 text-n-slate-10 disabled:opacity-30" :disabled="index === form.steps.length - 1" @click="move(form.steps, index, 1)" /><button type="button" title="Remover etapa" class="i-lucide-trash-2 size-4 text-n-ruby-9" @click="form.steps.splice(index, 1)" /></div></div>
              <div class="mt-3 grid gap-3 sm:grid-cols-2"><input v-model="step.title" required class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Titulo" /><input v-model="step.key" class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Chave opcional" /></div>
              <textarea v-model="step.entryConditions" rows="2" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Quando entrar nesta etapa" />
              <div class="mt-3 grid gap-3 sm:grid-cols-2"><input v-model="step.requiredFieldsText" class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Dados obrigatorios separados por virgula" /><input v-model="step.allowedActionsText" class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Acoes permitidas separadas por virgula" /></div>
              <textarea v-model="step.responseGuidelines" rows="2" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Como responder" />
              <textarea v-model="step.handoffConditions" rows="2" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Quando transferir para humano" />
              <label class="mt-3 flex items-center gap-2 text-sm text-n-slate-11"><input v-model="step.isActive" type="checkbox" /> Etapa ativa</label>
            </article>
          </section>

          <section v-else-if="activeTab === 'faq'" class="mt-5 space-y-4">
            <div class="flex items-center justify-between gap-3"><p class="text-sm text-n-slate-10">Respostas aprovadas para duvidas recorrentes.</p><button type="button" class="inline-flex items-center gap-2 text-sm text-n-brand" @click="form.faqs.push(emptyFaq())"><span class="i-lucide-plus size-4" /> Nova resposta</button></div>
            <article v-for="(faq, index) in form.faqs" :key="index" class="rounded-md border border-n-weak p-4">
              <div class="flex items-center justify-between gap-3"><strong class="text-sm text-n-slate-12">FAQ {{ index + 1 }}</strong><button type="button" title="Remover resposta" class="i-lucide-trash-2 size-4 text-n-ruby-9" @click="form.faqs.splice(index, 1)" /></div>
              <input v-model="faq.question" required class="mt-3 h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Pergunta principal" />
              <input v-model="faq.questionVariantsText" class="mt-3 h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Variações separadas por vírgula" />
              <label class="mt-3 block sm:max-w-48">
                <span class="mb-1 block text-sm text-n-slate-11">Prioridade</span>
                <input v-model.number="faq.priority" type="number" min="0" max="100" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
                <span class="mt-1 block text-xs text-n-slate-10">Use 0 a 100. Quanto maior, mais importante.</span>
              </label>
              <textarea v-model="faq.approvedAnswer" required rows="3" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Resposta aprovada" />
              <div class="mt-3 rounded-md border border-n-weak p-3">
                <div class="grid gap-3 sm:grid-cols-2">
                  <label class="block">
                    <span class="mb-1 block text-sm text-n-slate-11">Áudio aprovado vinculado</span>
                    <select v-model="faq.audioResourceFileId" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm">
                      <option value="">Sem áudio para esta FAQ</option>
                      <option v-for="audio in approvedAudioFiles" :key="audio.id" :value="audio.id">{{ audio.title }}</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-sm text-n-slate-11">Como usar o áudio</span>
                    <select v-model="faq.audioSendMode" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" :disabled="!faq.audioResourceFileId">
                      <option value="ask_permission">Perguntar antes de enviar</option>
                      <option value="send_when_requested">Enviar quando o cliente pedir áudio</option>
                      <option value="disabled">Não enviar áudio</option>
                    </select>
                  </label>
                </div>
                <audio v-if="faq.audioResourceFileId" :src="approvedAudioFiles.find(audio => audio.id === faq.audioResourceFileId)?.preview_url" controls preload="none" class="mt-3 h-8 max-w-full" />
                <p class="mt-2 text-xs text-n-slate-10">Para adicionar áudios, use a aba Recursos com tipo Áudio aprovado. Depois seleccione aqui em qual FAQ esse áudio deve ser usado.</p>
              </div>
              <label class="mt-3 flex items-center gap-2 text-sm text-n-slate-11"><input v-model="faq.isActive" type="checkbox" /> Resposta ativa</label>
            </article>
          </section>

          <section v-else-if="activeTab === 'resources'" class="mt-5 space-y-4">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">Enviar novo arquivo</h3>
              <p class="mt-1 text-sm text-n-slate-10">Adicione áudios aprovados, templates de foto ou documentos. O ficheiro já será vinculado ao protocolo actual.</p>
            </div>
            <div class="grid gap-3 rounded-md border border-n-weak p-4 sm:grid-cols-2">
              <select v-model="resourceUpload.resourceType" class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm">
                <option value="approved_audio">Áudio aprovado</option>
                <option value="photo_template">Template de foto</option>
                <option value="document">Documento PDF</option>
              </select>
              <input v-model="resourceUpload.title" class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Titulo do recurso" />
              <div class="sm:col-span-2"><SimpleTagPicker v-model="resourceUpload.tags" :suggestions="tagSuggestions" helper="Escolha em quais assuntos este material ajuda o atendimento." /></div>
              <input ref="resourceFileInput" type="file" accept="image/jpeg,image/png,image/webp,audio/mpeg,audio/mp4,audio/ogg,audio/wav,application/pdf" class="block w-full text-sm text-n-slate-11 sm:col-span-2" @change="chooseResourceFile" />
              <button type="button" class="inline-flex h-9 w-fit items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white disabled:opacity-60" :disabled="isUploadingResource" @click="uploadResource"><span class="i-lucide-upload size-4" /> {{ isUploadingResource ? 'Enviando...' : 'Enviar e vincular' }}</button>
            </div>

            <div>
              <h3 class="text-sm font-medium text-n-slate-12">Arquivos disponiveis</h3>
              <p class="mt-1 text-sm text-n-slate-10">Reutilize materiais enviados anteriormente.</p>
              <p v-if="resourceFiles.length === 0" class="mt-3 text-sm text-n-slate-10">Nenhum arquivo enviado.</p>
              <div v-else class="mt-3 grid gap-3 sm:grid-cols-2">
                <article v-for="item in resourceFiles" :key="item.id" class="flex items-start justify-between gap-3 rounded-md border border-n-weak p-3">
                  <div class="min-w-0"><strong class="block truncate text-sm text-n-slate-12">{{ item.title }}</strong><span class="mt-1 block text-xs text-n-slate-10">{{ resourceTypeLabel(item.resource_type) }}</span><audio v-if="item.resource_type === 'approved_audio'" :src="item.preview_url" controls preload="none" class="mt-2 h-8 max-w-full" /><img v-else-if="item.resource_type === 'photo_template'" :src="item.preview_url" :alt="item.title" class="mt-2 h-20 w-24 rounded object-cover" /></div>
                  <button type="button" class="shrink-0 text-sm text-n-brand disabled:text-n-slate-9" :disabled="resourceIsLinked(`attendance_resource:${item.id}`)" @click="useLibraryFile(item)">{{ resourceIsLinked(`attendance_resource:${item.id}`) ? 'Vinculado' : 'Usar' }}</button>
                </article>
              </div>
            </div>

            <div>
              <h3 class="text-sm font-medium text-n-slate-12">Galeria de resultados</h3>
              <p class="mt-1 text-sm text-n-slate-10">Escolha quais fotos reais podem apoiar este protocolo. O upload continua centralizado na Galeria de resultados.</p>
              <p v-if="resultImages.length === 0" class="mt-3 text-sm text-n-slate-10">Nenhuma foto cadastrada na galeria.</p>
              <div v-else class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <article v-for="item in resultImages.filter(image => image.is_active)" :key="item.id" class="overflow-hidden rounded-md border border-n-weak">
                  <img :src="item.preview_url" :alt="item.title" class="aspect-[4/3] w-full object-cover" />
                  <div class="flex items-start justify-between gap-2 p-3"><strong class="text-sm text-n-slate-12">{{ item.title }}</strong><button type="button" class="shrink-0 text-sm text-n-brand disabled:text-n-slate-9" :disabled="resourceIsLinked(`result_image:${item.id}`)" @click="useGalleryImage(item)">{{ resourceIsLinked(`result_image:${item.id}`) ? 'Vinculada' : 'Usar' }}</button></div>
                </article>
              </div>
            </div>

            <div>
              <h3 class="text-sm font-medium text-n-slate-12">Vinculados ao protocolo</h3>
              <p class="mt-1 text-sm text-n-slate-10">As tags ajudam o assistente a escolher o recurso mais proximo da pergunta do cliente.</p>
              <p v-if="form.resources.length === 0" class="mt-3 text-sm text-n-slate-10">Nenhum recurso vinculado.</p>
              <article v-for="(resource, index) in form.resources" :key="`${resource.reference}-${index}`" class="mt-3 grid gap-3 rounded-md border border-n-weak p-4 sm:grid-cols-[160px_1fr_auto]">
                <span class="text-sm text-n-slate-11">{{ resourceTypeLabel(resource.resourceType) }}</span>
                <div class="min-w-0"><strong class="block truncate text-sm text-n-slate-12">{{ resource.title }}</strong><div class="mt-2"><SimpleTagPicker v-model="resource.tags" :suggestions="tagSuggestions" helper="Marque os assuntos que ajudam o assistente a encontrar este material." /></div><label class="mt-2 flex items-center gap-2 text-sm text-n-slate-11"><input v-model="resource.isActive" type="checkbox" /> Disponivel para o assistente</label></div>
                <button type="button" title="Desvincular recurso" class="i-lucide-trash-2 size-4 text-n-ruby-9" @click="form.resources.splice(index, 1)" />
              </article>
            </div>
          </section>

          <section v-else-if="activeTab === 'simulation'" class="mt-5 space-y-4">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">Simulacao visual com IA</h3>
              <p class="mt-1 text-sm text-n-slate-10">Controle o convite, a coleta de fotos e o fallback usado pela assistente.</p>
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="form.visualSimulationConfig.enabled" type="checkbox" />
              Permitir simulacao visual neste protocolo
            </label>
            <label class="block">
              <span class="mb-1 block text-sm text-n-slate-11">Aviso mostrado antes do aceite</span>
              <textarea v-model="form.visualSimulationConfig.noticeText" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" />
            </label>
            <div>
              <span class="mb-2 block text-sm text-n-slate-11">Fotos solicitadas</span>
              <div class="grid gap-2 sm:grid-cols-2">
                <label v-for="angle in [{ value: 'front', label: 'Frente' }, { value: 'top', label: 'Topo' }, { value: 'left', label: 'Lateral esquerda' }, { value: 'right', label: 'Lateral direita' }]" :key="angle.value" class="flex items-center gap-2 rounded-md border border-n-weak p-3 text-sm text-n-slate-11">
                  <input v-model="form.visualSimulationConfig.requiredAngles" type="checkbox" :value="angle.value" />
                  {{ angle.label }}
                </label>
              </div>
            </div>
            <div class="rounded-md border border-n-weak p-4">
              <h4 class="text-sm font-medium text-n-slate-12">Exemplo enviado antes da coleta</h4>
              <p class="mt-1 text-sm text-n-slate-10">Escolha um template da biblioteca de recursos. A assistente envia a imagem ao cliente antes de pedir a primeira foto.</p>
              <select v-model="form.visualSimulationConfig.photoTemplateResourceId" class="mt-3 h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm">
                <option value="">Nao enviar imagem de exemplo</option>
                <option v-for="item in resourceFiles.filter(resource => resource.resource_type === 'photo_template' && resource.is_active)" :key="item.id" :value="item.id">{{ item.title }}</option>
              </select>
              <img v-if="form.visualSimulationConfig.photoTemplateResourceId" :src="resourceFiles.find(item => item.id === form.visualSimulationConfig.photoTemplateResourceId)?.preview_url" alt="Template selecionado para orientar as fotos" class="mt-3 aspect-square w-40 rounded-md border border-n-weak object-cover" />
              <label class="mt-3 block">
                <span class="mb-1 block text-sm text-n-slate-11">Legenda enviada com o exemplo</span>
                <textarea v-model="form.visualSimulationConfig.photoGuideCaption" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" />
              </label>
              <p class="mt-2 text-xs text-n-slate-10">Para adicionar outro modelo, use a aba Recursos e envie um arquivo do tipo Template de foto.</p>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Retencao das fotos em dias</span><input v-model.number="form.visualSimulationConfig.retentionDays" type="number" min="1" max="365" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Limite de variantes</span><input v-model.number="form.visualSimulationConfig.maxVariants" type="number" min="1" max="4" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            </div>
            <label class="block">
              <span class="mb-1 block text-sm text-n-slate-11">Mensagem quando a IA nao conseguir gerar</span>
              <textarea v-model="form.visualSimulationConfig.fallbackText" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" />
            </label>
          </section>

          <section v-else-if="activeTab === 'payment'" class="mt-5 space-y-4">
            <div class="flex items-center justify-between gap-3"><p class="text-sm text-n-slate-10">Guarde apenas a referencia segura do segredo, nunca dados bancarios no texto livre.</p><button type="button" class="inline-flex items-center gap-2 text-sm text-n-brand" @click="form.paymentProfiles.push(emptyPaymentProfile())"><span class="i-lucide-plus size-4" /> Novo perfil</button></div>
            <article v-for="(profile, index) in form.paymentProfiles" :key="index" class="rounded-md border border-n-weak p-4">
              <div class="grid gap-3 sm:grid-cols-2">
                <input v-model="profile.label" required class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Nome do perfil" />
                <div class="flex gap-2"><select v-model="profile.establishmentId" class="h-9 min-w-0 flex-1 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm"><option value="">Todas as unidades</option><option v-for="item in establishments" :key="item.id" :value="item.id">{{ item.name }}</option></select><button type="button" title="Remover perfil" class="i-lucide-trash-2 size-4 self-center text-n-ruby-9" @click="form.paymentProfiles.splice(index, 1)" /></div>
              </div>
              <textarea v-model="profile.instructionTemplate" required rows="3" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Instrucao sem dados sensiveis. Ex.: Vou enviar os dados seguros para confirmar o pedido." />
              <input v-model="profile.secretReference" class="mt-3 h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Referencia segura. Ex.: zero14/braga/pagamento-principal" />
              <label class="mt-3 flex items-center gap-2 text-sm text-n-slate-11"><input v-model="profile.isActive" type="checkbox" /> Perfil ativo</label>
            </article>
          </section>

          <section v-else class="mt-5 space-y-4">
            <div class="rounded-md border border-n-weak p-4">
              <h3 class="text-sm font-medium text-n-slate-12">Publicar rascunho</h3>
              <p class="mt-1 text-sm text-n-slate-10">A publicacao cria uma versao imutavel para auditoria e rollback.</p>
              <button type="button" class="mt-3 inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white disabled:opacity-60" :disabled="!selectedId || isSaving" @click="publish"><span class="i-lucide-send size-4" /> Publicar versao</button>
            </div>
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">Historico de versoes</h3>
              <p v-if="versions.length === 0" class="mt-3 text-sm text-n-slate-10">Nenhuma versao publicada.</p>
              <div v-else class="mt-3 space-y-2">
                <div v-for="version in versions" :key="version.id" class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-n-weak p-3">
                  <div><strong class="text-sm text-n-slate-12">Versao {{ version.version }}</strong><p class="mt-1 text-xs text-n-slate-10">{{ new Date(version.published_at).toLocaleString('pt-PT') }} por {{ version.published_by }}</p></div>
                  <button type="button" class="text-sm text-n-brand" @click="restore(version)">Restaurar como rascunho</button>
                </div>
              </div>
            </div>
          </section>

          <p v-if="message" class="mt-4 text-sm text-n-slate-11">{{ message }}</p>
          <div v-if="activeTab !== 'publication'" class="mt-5 border-t border-n-weak pt-4">
            <button class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white disabled:opacity-60" type="submit" :disabled="isSaving"><span class="i-lucide-save size-4" /> {{ isSaving ? 'Salvando...' : 'Salvar rascunho' }}</button>
          </div>
        </form>
      </main>
    </div>
  </div>
</template>
